/**
 * @fileoverview The When chips of both to-do sheets (E25): Today, This week and Pick a day, a radio group on one row
 * that never wraps, down to a 320px phone in English and Spanish (owner, 30 Oct). Each chip keeps its words on one line
 * (whitespace-nowrap) and the row never wraps (flex-nowrap); on a phone under 400px wide the chips draw a size smaller,
 * so "✓ Elegir un día" still fits beside "Hoy" and "Esta semana". A chip is a thumb tall; the picked one is filled with
 * a check.
 *
 * Where: OwnTodoSheet ("Add a to-do" and Edit), PlantSomethingSheet ("Plant something").
 * API: none.
 * Words (en/es): todo.own.today, todo.own.week, todo.own.day.
 *
 * @module own-todos/WhenChips
 */

import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { OwnWhen } from '@/shared/api/ownTodo';

/** A When chip: a thumb tall, its words on one line, a size smaller on the narrowest phones. */
const CHIP = 'inline-flex min-h-11 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border px-2 text-[0.8125rem] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[400px]:gap-1.5 min-[400px]:px-3 min-[400px]:text-[0.9375rem]';

/** WhenChips' props. */
interface WhenChipsProps {
  /** The chip picked. */
  value: OwnWhen;
  /** A chip was tapped. */
  onChange: (next: OwnWhen) => void;
  /** The id of the question the group answers ("When", "When will you plant it?"). */
  labelledBy: string;
}

/**
 * The three When chips on one row.
 *
 * @param props - WhenChipsProps.
 */
export const WhenChips: React.FC<WhenChipsProps> = ({ value, onChange, labelledBy }) => {
  const { reactTranslate: t } = useTranslation();
  const chips: Array<{ value: OwnWhen; text: string }> = [
    { value: 'today', text: t('todo.own.today') },
    { value: 'week', text: t('todo.own.week') },
    { value: 'day', text: t('todo.own.day') },
  ];
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="flex flex-nowrap gap-1 min-[400px]:gap-2">
      {chips.map((chip) => (
        <button
          key={chip.value}
          type="button"
          role="radio"
          aria-checked={value === chip.value}
          onClick={() => onChange(chip.value)}
          className={cn(CHIP, value === chip.value ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-muted')}
        >
          {value === chip.value && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
          {chip.text}
        </button>
      ))}
    </div>
  );
};
